// STS-shaped credentials via Cognito Identity Pools.
//
// The web-identity token we exchange is ALWAYS the id_token, never the
// access_token: AWS validates the JWT's `aud` claim, and Cognito puts `aud`
// in the id_token (the access_token carries `client_id` instead) — see
// CLAUDE.md pitfall #1. The Identity Pool's enhanced flow validates the
// User Pool token itself; the old direct-STS AssumeRoleWithWebIdentity path
// (and the IAM OIDC provider it needed) was retired platform-wide.

import { NotAuthorizedError } from "./errors.ts";

export interface StsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  /** ISO-8601 timestamp. */
  expiration: string;
}

export interface IdentityPoolExchangeArgs {
  region: string;
  identityPoolId: string;
  userPoolId: string;
  /** Cognito id_token — Identity Pools require the id_token (it carries the
   *  `aud` claim AWS validates), never the access_token. */
  idToken: string;
}

/** Get STS-style credentials via Cognito Identity Pools. The pool reads
 *  `cognito:groups` from the id_token and picks the IAM role based on
 *  its role-mapping rules. Two-call dance:
 *    1. GetId          — exchanges the id_token for an IdentityId.
 *    2. GetCredentialsForIdentity — exchanges IdentityId+token for creds.
 *  We talk to cognito-identity's regional endpoint (JSON-RPC over HTTPS,
 *  no AWS SDK dependency).
 */
export async function getCredentialsViaIdentityPool(
  args: IdentityPoolExchangeArgs,
): Promise<StsCredentials> {
  const providerKey = `cognito-idp.${args.region}.amazonaws.com/${args.userPoolId}`;
  const endpoint = `https://cognito-identity.${args.region}.amazonaws.com/`;

  async function call(target: string, body: object): Promise<unknown> {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityService.${target}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) {
      // Two different NotAuthorizedException shapes come back on this wire:
      //   - "Invalid login token. Token expired ..." → the token aged out
      //     (recoverable by a refresh; classified as expiry upstream).
      //   - "Access to Identity '...' is forbidden." → the pool's
      //     AmbiguousRoleResolution=Deny gate refused a VALID token (the user
      //     holds no role-mapped group). Refreshing can't fix that — surface
      //     it as a typed denial so UIs can show "not authorized".
      if (/NotAuthorizedException/.test(text) && /forbidden/i.test(text)) {
        throw new NotAuthorizedError(`cognito-identity ${target} ${res.status}: ${text}`);
      }
      throw new Error(`cognito-identity ${target} ${res.status}: ${text}`);
    }
    return JSON.parse(text);
  }

  const getId = (await call("GetId", {
    IdentityPoolId: args.identityPoolId,
    Logins: { [providerKey]: args.idToken },
  })) as { IdentityId?: string };
  if (!getId.IdentityId) throw new Error("GetId returned no IdentityId");

  const getCreds = (await call("GetCredentialsForIdentity", {
    IdentityId: getId.IdentityId,
    Logins: { [providerKey]: args.idToken },
  })) as {
    Credentials?: {
      AccessKeyId?: string;
      SecretKey?: string;
      SessionToken?: string;
      Expiration?: number;
    };
  };
  const c = getCreds.Credentials;
  if (!c?.AccessKeyId || !c?.SecretKey || !c?.SessionToken) {
    throw new Error("GetCredentialsForIdentity returned no credentials");
  }
  return {
    accessKeyId: c.AccessKeyId,
    secretAccessKey: c.SecretKey,
    sessionToken: c.SessionToken,
    // Cognito returns Expiration as a unix epoch (number); we normalize to ISO-8601.
    expiration: c.Expiration
      ? new Date(c.Expiration * 1000).toISOString()
      : new Date(Date.now() + 3600_000).toISOString(),
  };
}

/** Get UNAUTHENTICATED (visitor) credentials via Cognito Identity Pools — the
 *  same GetId + GetCredentialsForIdentity dance, but with NO `Logins`. The pool
 *  must allow unauthenticated identities; the returned creds are scoped to the
 *  pool's `unauthenticated` role ({slug}-visitor). Throws if the pool doesn't
 *  permit anonymous access. */
export async function getVisitorCredentialsViaIdentityPool(args: {
  region: string;
  identityPoolId: string;
}): Promise<StsCredentials> {
  const endpoint = `https://cognito-identity.${args.region}.amazonaws.com/`;
  async function call(target: string, body: object): Promise<unknown> {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityService.${target}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`cognito-identity ${target} ${res.status}: ${text}`);
    return JSON.parse(text);
  }

  const getId = (await call("GetId", { IdentityPoolId: args.identityPoolId })) as {
    IdentityId?: string;
  };
  if (!getId.IdentityId) throw new Error("GetId (visitor) returned no IdentityId");

  const getCreds = (await call("GetCredentialsForIdentity", {
    IdentityId: getId.IdentityId,
  })) as {
    Credentials?: {
      AccessKeyId?: string;
      SecretKey?: string;
      SessionToken?: string;
      Expiration?: number;
    };
  };
  const c = getCreds.Credentials;
  if (!c?.AccessKeyId || !c?.SecretKey || !c?.SessionToken) {
    throw new Error("GetCredentialsForIdentity (visitor) returned no credentials");
  }
  return {
    accessKeyId: c.AccessKeyId,
    secretAccessKey: c.SecretKey,
    sessionToken: c.SessionToken,
    expiration: c.Expiration
      ? new Date(c.Expiration * 1000).toISOString()
      : new Date(Date.now() + 3600_000).toISOString(),
  };
}
